import { useState, useCallback, useId, useEffect } from "react";

interface PulseQuestion {
  id: number;
  questionText: string;
  optionA: string;
  optionB: string;
  audience: string;
  responseCount: number;
}

interface PulseCardProps {
  audience: "artist" | "owner";
  fetchPulse: () => Promise<{ question: PulseQuestion | null; answered: boolean; treeCompleted?: boolean }>;
  postAnswer: (questionId: number, chosenOption: "a" | "b", sessionId: string) => Promise<{ ok: boolean; alreadyAnswered: boolean; branchQuestion: PulseQuestion | null }>;
  accent?: string;
}

function QuestionSlide({
  question,
  sessionId,
  postAnswer,
  accent,
  onAnswered,
}: {
  question: PulseQuestion;
  sessionId: string;
  postAnswer: PulseCardProps["postAnswer"];
  accent: string;
  onAnswered: (branch: PulseQuestion | null) => void;
}) {
  const [submitting, setSubmitting] = useState<"a" | "b" | null>(null);
  const [error, setError] = useState<string | null>(null);

  const pick = useCallback(async (option: "a" | "b") => {
    if (submitting) return;
    setSubmitting(option);
    setError(null);
    try {
      const result = await postAnswer(question.id, option, sessionId);
      onAnswered(result.branchQuestion);
    } catch {
      setError("Network error — tap to retry.");
      setSubmitting(null);
    }
  }, [submitting, question.id, sessionId, postAnswer, onAnswered]);

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 11, color: accent, textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700, marginBottom: 6 }}>
          Quick pulse — 2 seconds
        </div>
        <div style={{ fontSize: 15, fontWeight: 700, color: "#fff", lineHeight: 1.4 }}>
          {question.questionText}
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        {(["a", "b"] as const).map(opt => (
          <button
            key={opt}
            data-testid={`pulse-option-${opt}`}
            disabled={!!submitting}
            onClick={() => pick(opt)}
            style={{
              background: submitting === opt ? `${accent}22` : "#1a1a1a",
              border: `1.5px solid ${submitting === opt ? accent : "#333"}`,
              borderRadius: 8,
              color: submitting === opt ? accent : "#ccc",
              fontSize: 13,
              fontWeight: 600,
              padding: "10px 14px",
              cursor: submitting ? "not-allowed" : "pointer",
              opacity: submitting && submitting !== opt ? 0.5 : 1,
              textAlign: "center",
              transition: "all 0.15s ease",
              lineHeight: 1.3,
            }}
          >
            {submitting === opt ? "…" : (opt === "a" ? question.optionA : question.optionB)}
          </button>
        ))}
      </div>
      {error && <div style={{ color: "#ef4444", fontSize: 11, marginTop: 8 }}>{error}</div>}
    </div>
  );
}

const THANK_YOU_DURATION_MS = 2500;

export function PulseCard({ audience, fetchPulse, postAnswer, accent = "#f59e0b" }: PulseCardProps) {
  const sessionId = useId().replace(/:/g, "");
  const [state, setState] = useState<
    | { phase: "loading" }
    | { phase: "no-question" }
    | { phase: "question"; question: PulseQuestion }
    | { phase: "done" }
    | { phase: "branch"; question: PulseQuestion }
    | { phase: "error" }
  >({ phase: "loading" });

  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    if (loaded) return;
    setLoaded(true);
    try {
      const data = await fetchPulse();
      if (!data.question) {
        setState({ phase: "no-question" });
        return;
      }
      setState({ phase: "question", question: data.question });
    } catch {
      setState({ phase: "error" });
    }
  }, [loaded, fetchPulse]);

  if (!loaded) {
    load();
  }

  const handleRootAnswer = useCallback((branch: PulseQuestion | null) => {
    if (branch) {
      setState({ phase: "branch", question: branch });
    } else {
      setState({ phase: "done" });
    }
  }, []);

  const handleBranchAnswer = useCallback(() => {
    setState({ phase: "done" });
  }, []);

  const handleDismiss = useCallback(() => {
    setState({ phase: "no-question" });
  }, []);

  // Auto-collapse the thank-you confirmation after a short delay.
  useEffect(() => {
    if (state.phase !== "done") return;
    const timer = setTimeout(() => {
      setState({ phase: "no-question" });
    }, THANK_YOU_DURATION_MS);
    return () => clearTimeout(timer);
  }, [state.phase]);

  if (state.phase === "loading") return null;
  if (state.phase === "no-question") return null;
  if (state.phase === "error") return null;
  if (state.phase === "done") {
    return (
      <div
        data-testid="pulse-card-done"
        style={{
          background: "#111",
          border: "1px solid #2a2a2a",
          borderRadius: 12,
          padding: "14px 20px",
          fontSize: 13,
          color: "#22c55e",
          fontWeight: 600,
          animation: "pulse-fade-out 0.4s ease forwards",
          animationDelay: `${THANK_YOU_DURATION_MS - 400}ms`,
        }}
      >
        <style>{`
          @keyframes pulse-fade-out {
            from { opacity: 1; }
            to { opacity: 0; }
          }
        `}</style>
        ✓ Thanks for your input!
      </div>
    );
  }

  return (
    <div
      data-testid="pulse-card"
      style={{
        background: "#111",
        border: "1px solid #2a2a2a",
        borderRadius: 12,
        padding: "18px 20px",
        position: "relative",
      }}
    >
      <button
        data-testid="pulse-dismiss"
        onClick={handleDismiss}
        aria-label="Dismiss"
        title="Dismiss"
        style={{
          position: "absolute",
          top: 10,
          right: 10,
          background: "none",
          border: "none",
          color: "#555",
          cursor: "pointer",
          padding: "2px 6px",
          fontSize: 16,
          lineHeight: 1,
          borderRadius: 4,
          transition: "color 0.15s ease",
        }}
        onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.color = "#999"; }}
        onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.color = "#555"; }}
      >
        ×
      </button>
      {state.phase === "question" && (
        <QuestionSlide
          question={state.question}
          sessionId={sessionId}
          postAnswer={postAnswer}
          accent={accent}
          onAnswered={handleRootAnswer}
        />
      )}
      {state.phase === "branch" && (
        <div>
          <div style={{ fontSize: 11, color: "#555", marginBottom: 10 }}>
            Follow-up based on your answer:
          </div>
          <QuestionSlide
            question={state.question}
            sessionId={sessionId}
            postAnswer={postAnswer}
            accent={accent}
            onAnswered={() => handleBranchAnswer()}
          />
        </div>
      )}
    </div>
  );
}
